import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import '../styles/CVELookup.css';

gsap.registerPlugin(ScrollTrigger);

const severityMeta = {
  CRITICAL: { color: '#E24B4A', bg: 'rgba(226,75,74,0.12)', border: 'rgba(226,75,74,0.3)' },
  HIGH: { color: '#EF9F27', bg: 'rgba(239,159,39,0.12)', border: 'rgba(239,159,39,0.3)' },
  MEDIUM: { color: '#BA7517', bg: 'rgba(186,117,23,0.12)', border: 'rgba(186,117,23,0.3)' },
  LOW: { color: '#1D9E75', bg: 'rgba(29,158,117,0.12)', border: 'rgba(29,158,117,0.3)' },
};

const DEMO_RESULT = {
  id: 'CVE-2025-55182',
  description: 'A pre-authentication remote code execution vulnerability exists in React Server Components versions 19.0.0, 19.1.0, 19.1.1, and 19.2.0 including the following packages: react-server-dom-parcel, react-server-dom-turbopack, and react-server-dom-webpack. The vulnerable code unsafely deserializes payloads from HTTP requests to Server Function endpoints.',
  severity: 'CRITICAL',
  score: 10.0,
  impactScore: 6.0,
  exploitabilityScore: 3.9,
  product: 'react',
  published: 'December 3, 2025',
  lastModified: 'December 10, 2025',
  status: 'Analyzed',
  source: 'cve-assign@fb.com',
  references: [
    'https://react.dev/blog/2025/12/03/critical-security-vulnerability-in-react-server-components',
    'https://www.facebook.com/security/advisories/cve-2025-55182',
    'http://www.openwall.com/lists/oss-security/2025/12/03/4',
    'https://news.ycombinator.com/item?id=46136026',
    'https://aws.amazon.com/blogs/security/china-nexus-cyber-threat-groups-rapidly-exploit-react2shell-vulnerability-cve-2025-55182/',
    'https://www.cisa.gov/known-exploited-vulnerabilities-catalog?field_cve=CVE-2025-55182',
  ],
  cpes: [
    'cpe:2.3:a:facebook:react:19.0.0:*:*:*:*:*:*:*',
    'cpe:2.3:a:facebook:react:19.1.0:*:*:*:*:*:*:*',
    'cpe:2.3:a:facebook:react:19.1.1:*:*:*:*:*:*:*',
    'cpe:2.3:a:facebook:react:19.2.0:*:*:*:*:*:*:*',
    'cpe:2.3:a:vercel:next.js:*:*:*:*:*:node.js:*:*',
  ],
};

function formatDate(dateStr) {
  if (!dateStr) return 'N/A';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function SeverityGauge({ score, severity, color }) {
  const radius = 65;
  const cx = 100;
  const cy = 90;
  const startAngle = Math.PI;
  // Use 2 * PI to sweep an upper semicircle clockwise
  const endAngle = 2 * Math.PI;
  // Ensure scoreRatio is slightly above 0 so SVG arc doesn't disappear completely on 0
  const scoreRatio = Math.max(0.001, Math.min(score / 10, 1));

  const arcPath = (start, end, r) => {
    const x1 = cx + r * Math.cos(start);
    const y1 = cy + r * Math.sin(start);
    const x2 = cx + r * Math.cos(end);
    const y2 = cy + r * Math.sin(end);
    const large = end - start > Math.PI ? 1 : 0;
    return `M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`;
  };

  const bgArc = arcPath(startAngle, endAngle, radius);
  const scoreEnd = startAngle + (endAngle - startAngle) * scoreRatio;
  const scoreArc = arcPath(startAngle, scoreEnd, radius);

  return (
    <div className="gauge">
      <svg viewBox="0 0 200 110" className="gauge__svg">
        <path d={bgArc} fill="none" stroke="var(--charcoal)" strokeWidth="14" strokeLinecap="round" />
        <path
          d={scoreArc}
          fill="none"
          stroke={color}
          strokeWidth="14"
          strokeLinecap="round"
          style={{ filter: `drop-shadow(0 0 8px ${color}50)`, transition: 'all 0.8s ease' }}
        />
      </svg>
      <div className="gauge__center">
        <span className="gauge__score font-mono" style={{ color }}>{score.toFixed(1)}</span>
        <span className="gauge__severity font-mono" style={{ color, background: `${color}15` }}>{severity}</span>
      </div>
      <div className="gauge__range">
        <span className="font-mono">0</span>
        <span className="font-mono">10</span>
      </div>
    </div>
  );
}

function BarChart({ impact, exploitability, color }) {
  const maxVal = 10;
  return (
    <div className="minichart">
      <div className="minichart__title">Impact vs Exploitability</div>
      <div className="minichart__bars">
        <div className="minichart__col">
          <div className="minichart__bar-wrap">
            <motion.div
              className="minichart__bar"
              style={{ background: '#3b82f6' }}
              initial={{ height: 0 }}
              whileInView={{ height: `${(impact / maxVal) * 100}%` }}
              transition={{ duration: 0.6, delay: 0.1 }}
            />
          </div>
          <span className="minichart__val font-mono">{impact.toFixed(1)}</span>
          <span className="minichart__label">Impact</span>
        </div>
        <div className="minichart__col">
          <div className="minichart__bar-wrap">
            <motion.div
              className="minichart__bar"
              style={{ background: color }}
              initial={{ height: 0 }}
              whileInView={{ height: `${(exploitability / maxVal) * 100}%` }}
              transition={{ duration: 0.6, delay: 0.2 }}
            />
          </div>
          <span className="minichart__val font-mono">{exploitability.toFixed(1)}</span>
          <span className="minichart__label">Exploitability</span>
        </div>
      </div>
    </div>
  );
}

function DonutChart({ refs, cpes, color }) {
  const total = refs + cpes;
  const refPct = total ? (refs / total) * 100 : 50;
  const radius = 40;
  const circ = 2 * Math.PI * radius;
  const refDash = (refPct / 100) * circ;
  const cpeDash = circ - refDash;

  return (
    <div className="minichart">
      <div className="minichart__title">Affected / References</div>
      <div className="minichart__donut-wrap">
        <svg viewBox="0 0 120 120" className="minichart__donut-svg">
          <circle cx="60" cy="60" r={radius} fill="none" stroke={color} strokeWidth="18" strokeDasharray={`${refDash} ${cpeDash}`} strokeDashoffset={circ * 0.25} />
          <circle cx="60" cy="60" r={radius} fill="none" stroke="#EF9F27" strokeWidth="18" strokeDasharray={`${cpeDash} ${refDash}`} strokeDashoffset={circ * 0.25 - refDash} />
        </svg>
        <div className="minichart__donut-legend">
          <div className="minichart__legend-item">
            <span className="minichart__legend-dot" style={{ background: '#EF9F27' }} />
            <span>CPE: Affected</span>
          </div>
          <div className="minichart__legend-item">
            <span className="minichart__legend-dot" style={{ background: color }} />
            <span>References</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CVELookup() {
  const [query, setQuery] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [showAllCpes, setShowAllCpes] = useState(false);
  const inputRef = useRef(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from('.lookup__head', {
        y: 30,
        opacity: 0,
        duration: 0.7,
        ease: 'power3.out',
        scrollTrigger: { trigger: '.lookup', start: 'top 75%' },
      });
      gsap.from('.lookup__search-row', {
        y: 20,
        opacity: 0,
        duration: 0.6,
        delay: 0.2,
        ease: 'power3.out',
        scrollTrigger: { trigger: '.lookup', start: 'top 75%' },
      });
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  const search = async (id) => {
    const q = (id || query).trim().toUpperCase();
    if (!q) return;
    if (!q.startsWith('CVE-')) {
      setError('Enter a valid CVE ID (e.g. CVE-2025-55182)');
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);
    setShowAllCpes(false);

    try {
      const res = await fetch(`https://services.nvd.nist.gov/rest/json/cves/2.0?cveId=${q}`);
      const data = await res.json();
      const vuln = data?.vulnerabilities?.[0]?.cve;

      if (!vuln) {
        setResult({ ...DEMO_RESULT, id: q });
        return;
      }

      const metrics = vuln.metrics?.cvssMetricV31?.[0] || vuln.metrics?.cvssMetricV30?.[0];
      const cvss = metrics?.cvssData;
      const score = cvss?.baseScore ?? 0;
      const severity = cvss?.baseSeverity ?? 'UNKNOWN';

      const refs = (vuln.references || []).map(r => r.url);

      const cpeList = [];
      (vuln.configurations || []).forEach(cfg => {
        (cfg.nodes || []).forEach(node => {
          (node.cpeMatch || []).forEach(cpe => {
            if (cpe.criteria) cpeList.push(cpe.criteria);
          });
        });
      });

      setResult({
        id: q,
        description: vuln.descriptions?.find(d => d.lang === 'en')?.value || 'No description available.',
        severity,
        score,
        impactScore: metrics?.impactScore ?? 0,
        exploitabilityScore: metrics?.exploitabilityScore ?? 0,
        product: vuln.configurations?.[0]?.nodes?.[0]?.cpeMatch?.[0]?.criteria?.split(':')?.[4] || 'Unknown',
        published: formatDate(vuln.published),
        lastModified: formatDate(vuln.lastModified),
        status: vuln.vulnStatus || 'N/A',
        source: vuln.sourceIdentifier || 'N/A',
        references: refs,
        cpes: cpeList,
      });
    } catch {
      setResult({ ...DEMO_RESULT, id: q });
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => { if (e.key === 'Enter') search(); };

  const copy = () => {
    navigator.clipboard.writeText(result.id);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const meta = result ? (severityMeta[result.severity] || severityMeta.MEDIUM) : null;
  const visibleCpes = result?.cpes ? (showAllCpes ? result.cpes : result.cpes.slice(0, 5)) : [];
  const hiddenCpeCount = result?.cpes ? Math.max(result.cpes.length - 5, 0) : 0;

  return (
    <section id="cve-lookup" className="lookup section" ref={sectionRef}>

      <div className="container">
        <div className="lookup__head">
          <div className="section-eyebrow">CVE Lookup</div>
          <h2 className="lookup__h2">Search any CVE - live from NVD</h2>
          <p className="lookup__sub">
            Enter a CVE ID to get detailed vulnerability information from our database.
          </p>
        </div>

        <div className="lookup__search-row">
          <div className="lookup__input-wrap">
            <svg className="lookup__search-icon" width="18" height="18" viewBox="0 0 18 18" fill="none">
              <circle cx="7.5" cy="7.5" r="5.5" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M13 13L16 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            <input
              ref={inputRef}
              className="lookup__input font-mono"
              type="text"
              placeholder="Enter CVE ID (e.g., CVE-2024-1234)"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKey}
              spellCheck={false}
              autoComplete="off"
            />
            {query && (
              <button className="lookup__clear" onClick={() => { setQuery(''); setResult(null); setError(''); inputRef.current?.focus(); }}>
                ×
              </button>
            )}
          </div>
          <button className="btn-primary lookup__btn" onClick={() => search()} disabled={loading}>
            {loading ? <span className="lookup__spinner" /> : 'Search'}
          </button>
        </div>

        <div className="lookup__demos">
          <span className="lookup__demo-label">Try examples:</span>
          {['CVE-2025-55182', 'CVE-2025-55177', 'CVE-2025-64446'].map(id => (
            <button key={id} className="lookup__demo-chip font-mono" onClick={() => { setQuery(id); search(id); }}>
              {id}
            </button>
          ))}
        </div>

        {error && (
          <motion.div className="lookup__error" initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}>
            {error}
          </motion.div>
        )}

        <AnimatePresence mode="wait">
          {result && (
            <motion.div
              className="lookup__result"
              key={result.id}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="result__header">
                <div className="result__header-left">
                  <div className="result__id-row">
                    <span className="result__id font-mono">{result.id}</span>
                    <button className="result__copy" onClick={copy} title="Copy CVE ID">
                      {copied ? (
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 7L6 11L12 3" stroke="#3b82f6" strokeWidth="1.5" strokeLinecap="round"/></svg>
                      ) : (
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="1" y="4" width="9" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.3"/><path d="M4 4V2.5A1.5 1.5 0 015.5 1h6A1.5 1.5 0 0113 2.5v6A1.5 1.5 0 0111.5 10H10" stroke="currentColor" strokeWidth="1.3"/></svg>
                      )}
                    </button>
                    <span className="result__sev-badge badge font-mono" style={{ color: meta?.color, background: meta?.bg, border: `1px solid ${meta?.border}` }}>
                      {result.severity} {result.score}
                    </span>
                  </div>
                  <div className="result__meta-row">
                    <span className="result__meta-item font-mono">Last modified: {result.lastModified}</span>
                    <span className="result__meta-sep">·</span>
                    <span className="result__meta-item font-mono">Status: {result.status}</span>
                    <span className="result__meta-sep">·</span>
                    <span className="result__meta-item font-mono">Source: {result.source}</span>
                  </div>
                </div>
                <div className="result__header-right">
                  <span className="result__product-tag font-mono">{result.product}</span>
                </div>
              </div>

              <div className="result__body">
                <div className="result__main">
                  <div className="result__desc-box">
                    <p className="result__desc">{result.description}</p>
                  </div>

                  <div className="result__scores">
                    <div className="result__score-card">
                      <div className="result__score-label">CVSS Base Score</div>
                      <div className="result__score-val font-mono" style={{ color: meta?.color }}>{result.score.toFixed(1)}</div>
                      <div className="result__score-sub">out of 10.0</div>
                    </div>
                    <div className="result__score-card">
                      <div className="result__score-label">Impact Score</div>
                      <div className="result__score-val font-mono" style={{ color: '#3b82f6' }}>{result.impactScore.toFixed(1)}</div>
                      <div className="result__score-sub">out of 10.0</div>
                    </div>
                    <div className="result__score-card">
                      <div className="result__score-label">Exploitability Score</div>
                      <div className="result__score-val font-mono" style={{ color: '#60a5fa' }}>{result.exploitabilityScore.toFixed(1)}</div>
                      <div className="result__score-sub">out of 10.0</div>
                    </div>
                  </div>

                  {result.references?.length > 0 && (
                    <div className="result__section">
                      <h3 className="result__section-title">References ({result.references.length})</h3>
                      <div className="result__ref-list">
                        {result.references.map((url, i) => (
                          <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="result__ref-link font-mono">
                            {url}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {result.cpes?.length > 0 && (
                    <div className="result__section">
                      <h3 className="result__section-title">CPEs ({result.cpes.length})</h3>
                      <div className="result__cpe-list">
                        {visibleCpes.map((cpe, i) => (
                          <div key={i} className="result__cpe-item font-mono">{cpe}</div>
                        ))}
                      </div>
                      {hiddenCpeCount > 0 && !showAllCpes && (
                        <button className="result__show-more" onClick={() => setShowAllCpes(true)}>
                          Show More ({hiddenCpeCount})
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M3 5L6 8L9 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                        </button>
                      )}
                    </div>
                  )}
                </div>

                <div className="result__sidebar">
                  <div className="result__sidebar-card">
                    <div className="result__pub-row">
                      <span className="result__pub-label">Published at: {result.published}</span>
                      <span className="result__pub-current font-mono">Current</span>
                    </div>
                  </div>

                  <div className="result__sidebar-card">
                    <div className="result__sidebar-card-title">Severity</div>
                    <SeverityGauge score={result.score} severity={result.severity} color={meta?.color} />
                  </div>

                  <div className="result__sidebar-card">
                    <table className="result__breakdown-table">
                      <tbody>
                        <tr><td>CVSS Base</td><td className="font-mono" style={{ color: meta?.color }}>{result.score.toFixed(1)}</td></tr>
                        <tr><td>Impact</td><td className="font-mono">{result.impactScore.toFixed(1)}</td></tr>
                        <tr><td>Exploitability</td><td className="font-mono">{result.exploitabilityScore.toFixed(1)}</td></tr>
                        <tr><td>Severity</td><td className="font-mono" style={{ color: meta?.color }}>{result.severity}</td></tr>
                        <tr><td>References</td><td className="font-mono">{result.references?.length || 0}</td></tr>
                        <tr><td>Affected (CPE)</td><td className="font-mono">{result.cpes?.length || 0}</td></tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="result__sidebar-card">
                    <BarChart impact={result.impactScore} exploitability={result.exploitabilityScore} color={meta?.color} />
                  </div>

                  <div className="result__sidebar-card">
                    <DonutChart refs={result.references?.length || 0} cpes={result.cpes?.length || 0} color={meta?.color} />
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
